<template>
	<div class="news-pagination">
		<nuxt-link
			:to="isFirstPage ? undefined : createURL(currentPage - 1)"
			class="news-pagination__arrow news-pagination__arrow--previous"
			:class="{ 'news-pagination__arrow--previous-disabled': isFirstPage }">
			<GlobalIcon name="arrow-left" :height="16" />
		</nuxt-link>
		<div
			v-for="page in paginationPages"
			:key="page.pageNumber"
			class="news-pagination__page"
			:class="{ 'news-pagination__page--current': page.isCurrent }">
			<nuxt-link v-if="!page.isDots && !page.isOf" :to="createURL(page.pageNumber)">
				{{ page.pageNumber }}
			</nuxt-link>
			<span v-else-if="page.isDots">...</span>
			<span v-else>{{ $t('common.of') }}</span>
		</div>
		<nuxt-link
			:to="isLastPage ? undefined : createURL(currentPage + 1)"
			class="news-pagination__arrow news-pagination__arrow--next"
			:class="{ 'news-pagination__arrow--next-disabled': isLastPage }">
			<GlobalIcon name="arrow-right" :height="16" />
		</nuxt-link>
	</div>
</template>

<script setup lang="ts">
import type { RouteLocationRaw } from 'vue-router'

const $t = useI18n().t

const props = defineProps<{
	currentPage: number
	maxPage: number
	createURL: (page: number) => RouteLocationRaw
}>()

interface IPaginationPage {
	isDots?: boolean
	isOf?: boolean
	isCurrent?: boolean
	pageNumber: number
}

const isFirstPage = computed(() => props.currentPage === 1)
const isLastPage = computed(() => props.currentPage === props.maxPage)

const paginationPages = computed<IPaginationPage[]>(() => {
	const maxPagesToShow = 5
	const pages: IPaginationPage[] = []
	let startPage = 1
	let endPage = props.maxPage

	if (props.maxPage > maxPagesToShow) {
		const middle = Math.ceil(maxPagesToShow / 2)
		startPage = props.currentPage - middle + 1
		endPage = props.currentPage + middle - 1

		if (startPage < 1) {
			startPage = 1
			endPage = maxPagesToShow
		} else if (endPage > props.maxPage) {
			startPage = props.maxPage - maxPagesToShow + 1
			endPage = props.maxPage
		}
	}

	if (startPage > 1) {
		pages.push({ isDots: false, isCurrent: false, pageNumber: 1 })
		if (startPage > 2) pages.push({ isDots: true, isCurrent: false, pageNumber: 0 })
	}

	for (let i = startPage; i <= endPage; i++) {
		pages.push({ isDots: false, isCurrent: i === props.currentPage, pageNumber: i })
	}

	if (endPage < props.maxPage) {
		pages.push({ isOf: true, isCurrent: false, pageNumber: 0 })
		pages.push({ isDots: false, isCurrent: false, pageNumber: props.maxPage })
	}

	return pages
})
</script>

<style lang="scss" scoped src="./Pagination.scss"></style>
